import { ref, string } from 'yup';
import { capitalizeFirstLetter } from './functions';
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from 'react-phone-number-input';

export const requiredField = ({ fieldName = '', message }) => {
  return string().required(
    message ?? `${capitalizeFirstLetter(fieldName)} is required`,
  );
};

export const email = ({
  fieldName = '',
  message = 'Invalid email',
  isRequired = true,
}) => {
  if (isRequired) {
    return requiredField({ fieldName }).email(message);
  } else {
    return string().email(message);
  }
};

// .matches(/^[a-zA-Z0-9\s\-_'@]+$/, "only letters, numbers, spaces, hyphens, underscores, @, and single quotes are allowed")
export const name = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 50,
  regex = /^(?=.*[a-zA-Z])[a-zA-Z0-9\s\-_.(){}[\]\\/]+$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        `${capitalizeFirstLetter(fieldName)} contains either alphabetic or alpha numeric characters eg. ${capitalizeFirstLetter(fieldName)}123`,
      )
      .test(
        'not-only-spaces',
        `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`,
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        .test(
          'not-only-spaces',
          `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`,
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const username = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 50,
  regex = /^[a-zA-Z0-9\s\-_.(){}[\]\\/'":]+$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        `Space are not allowed, ${capitalizeFirstLetter(fieldName)} contains either alphabetic or alpha numeric characters eg. ${capitalizeFirstLetter(fieldName)}123.`,
      )
      .test(
        'not-only-spaces',
        `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`,
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        .test(
          'not-only-spaces',
          `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`,
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const password = ({
  fieldName = '',
  message = 'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character',
  regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%^&+=!_]).{8,}$/,
  isRequired = true,
}) => {
  if (isRequired) {
    return requiredField({ fieldName }).matches(regex, message);
  } else {
    return string().matches(regex, message);
  }
};

export const confirmPassword = ({
  fieldName = '',
  referenceField = 'password', // Name of the field to match
  message = 'Passwords must match',
  isRequired = true,
}) => {
  if (isRequired) {
    return requiredField({ fieldName }).oneOf(
      [ref(referenceField), null],
      message,
    );
  } else {
    return string().oneOf([ref(referenceField), null], message);
  }
};

export const phone = ({
  fieldName = '',
  message = 'Must be valid phone number',
  isRequired = true,
}) => {
  if (isRequired) {
    return requiredField({ fieldName }).test({
      message,
      test: (value) => {
        return isPossiblePhoneNumber(value) && isValidPhoneNumber(value);
      },
    });
  } else {
    return string().notRequired();
  }
};

export const zipcode = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 6,
  max = 8,
  regex = /^[a-zA-Z0-9\s]*$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        'Pincode contains either numeric or alphanumeric e.g. 123456 or ZIP123',
      )
      .test(
        'not-only-spaces',
        'Pincode cannot consist only of spaces',
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        // .matches(
        //   regex,
        //   'Pincode contains either numeric or alphanumeric eg. 123456 or ZIP123',
        // )
        .test(
          'not-only-spaces',
          'Pincode cannot consist only of spaces',
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const company = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 50,
  regex = /^[a-zA-Z0-9\s]*$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        'Company name contains either alphabetic or alphanumeric eg. Company123',
      )
      .test(
        'not-only-spaces',
        'Company name cannot consist only of spaces',
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        .test(
          'not-only-spaces',
          'Company name cannot consist only of spaces',
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const address = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 100,
  regex = /^[a-zA-Z0-9,.\-\s/\\]*$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        'Address can contains only letters, numbers, commas, hyphens, and periods',
      )
      .test(
        'not-only-spaces',
        'Address cannot consist only of spaces',
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        // .matches(
        //   regex,
        //   'Address can contains only letters, numbers, commas, hyphens, and periods',
        // )
        .test(
          'not-only-spaces',
          'Address cannot consist only of spaces',
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const fax = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 10,
  regex = /^[0-9+\-().\s]*$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        'Fax can contains only number, Plus Sign, Hyphen, parenthesis and period',
      )
      .test(
        'not-only-spaces',
        'Fax cannot consist only of spaces',
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        // .matches(
        //   regex,
        //   'Fax can contains only number, Plus Sign, Hyphen, parenthesis and period',
        // )
        .test(
          'not-only-spaces',
          'Fax cannot consist only of spaces',
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const ipAddress = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 12,
  max = 16,
  regex = /^[0-9.]+$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(regex, `${fieldName} can contains only number and period`)
      .test(
        'not-only-spaces',
        `${fieldName} cannot consist only of spaces`,
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        // .matches(regex, `${fieldName} can contains only number and period`)
        .test(
          'not-only-spaces',
          `${fieldName} cannot consist only of spaces`,
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const macAddress = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 10,
  regex = /^[0-9+\-().\s]*$/,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .matches(
        regex,
        'Fax can contains only number, Plus Sign, Hyphen, parenthesis and period',
      )
      .test(
        'not-only-spaces',
        'Fax cannot consist only of spaces',
        (value) => value?.trim().length > 0,
      );
  } else {
    return (
      string()
        // .matches(
        //   regex,
        //   'Fax can contains only number, Plus Sign, Hyphen, parenthesis and period',
        // )
        .test(
          'not-only-spaces',
          'Fax cannot consist only of spaces',
          (value) =>
            value === undefined || value === '' || value?.trim().length > 0,
        )
    );
  }
};

export const url = ({
  fieldName = "",
  // message = '',
  isRequired = true,
  min = 18,
  max = 100,
}) => {
  if (isRequired) {
    return requiredField({ fieldName })
      .min(min, `Too short, minimum length is ${min} characters`)
      .max(max, `Too long, maximum length is ${max} characters`)
      .url("Must be a valid URL")
      .test("not-only-spaces", `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`, (value) => {
        if (value === undefined) {
          return false;
        }
        return value?.trim().length > 0;
      });
  } else {
    return string().test(
      "not-only-spaces",
      `${capitalizeFirstLetter(fieldName)} cannot consist only of spaces`,
      (value) => value === undefined || value === "" || value?.trim().length > 0
    );
  }
};

export const common = ({
  fieldName = '',
  message = '',
  isRequired = true,
  min = 2,
  max = 50,
  regex = /^[0-9+\-().\s]*$/,
}) => {
  if (isRequired) {
    return (
      requiredField({ fieldName })
        .min(min, `Too short, minimum length is ${min} characters`)
        .max(max, `Too long, maximum length is ${max} characters`)
        .test(
          'not-only-spaces',
          'Cannot consist only of spaces',
          (value) => value?.trim().length > 0,
        )
    );
  } else {
    return string().test(
      'not-only-spaces',
      'Cannot consist only of spaces',
      (value) =>
        value === undefined || value === '' || value?.trim().length > 0,
    );
  }
};
